import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Typography,
} from "@mui/material";
import {
  getReportDownloadLabel,
  reportDownloadCompactSx,
} from "screens/Reports/helpers/reportsToolbarStyles";
import reportsStyles from "../Reports.styles";
import { reportsRecordListTokens } from "../Reports.tokens";
import { CheckGlyph, ChevronGlyph, DownloadGlyph } from "./ReportsIcons";
import EmptyState from "global/components/EmptyState/EmptyState";

const styles = reportsStyles;
const { checkbox, pager } = reportsRecordListTokens;

export interface ReportRecordPair {
  label: string;
  value: string;
}

/** One row of a report, flattened to what the phone/tablet card can show. */
export interface ReportRecord {
  rowId: string;
  name: string;
  /** Second line — the email, the creator, the sender. Omitted when unknown. */
  sub?: string;
  /** Names this record's selection control for screen readers (WCAG 4.1.2). */
  selectionLabel: string;
  /** The table's numeric columns, as "label: value" chips. */
  pairs: ReportRecordPair[];
}

interface ReportsRecordListProps {
  records: ReportRecord[];
  selected: string[];
  onSelectOne: (rowId: string) => void;
  onDeselectOne: (rowId: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  isLoading: boolean;
  onDownload: () => void;
  emptyTitle: string;
  emptySub: string;
  /** Names the list itself, since it no longer has a table caption. */
  ariaLabel: string;
  page: number;
  rowsPerPage: number;
  count: number;
  onPageChange: (page: number) => void;
}

const checkGlyphSx = {
  width: { xs: checkbox.glyphSize.phone, sm: checkbox.glyphSize.tablet },
  height: { xs: checkbox.glyphSize.phone, sm: checkbox.glyphSize.tablet },
};

/**
 * The phone and tablet stand-in for `CustomTable` on a Reports tab.
 *
 * `Reports - Mobile & Tablet.dc.html` drops the table at these widths: a phone
 * gets one card per record with the numeric columns as chips beneath the name,
 * and the tablet closes those cards back into a single bordered list with the
 * chips pushed to the right. Both are the same three cells — checkbox, text,
 * chips — placed differently by `styles.recordRow`, so one component draws them.
 *
 * Selection, download and paging all call straight back into the view's own
 * handlers; nothing about how a report is fetched or exported changes here.
 */
const ReportsRecordList = ({
  records,
  selected,
  onSelectOne,
  onDeselectOne,
  onSelectAll,
  onDeselectAll,
  isLoading,
  onDownload,
  emptyTitle,
  emptySub,
  ariaLabel,
  page,
  rowsPerPage,
  count,
  onPageChange,
}: ReportsRecordListProps) => {
  const selectedCount = records.filter((record) =>
    selected.includes(record.rowId),
  ).length;
  const allSelected = records.length > 0 && selectedCount === records.length;
  const someSelected = selectedCount > 0 && !allSelected;

  const totalPages = Math.max(1, Math.ceil(count / Math.max(rowsPerPage, 1)));
  const showPager = count > rowsPerPage;
  const firstOnPage = (page - 1) * rowsPerPage + 1;
  const lastOnPage = Math.min(page * rowsPerPage, count);

  if (isLoading) {
    return (
      <Box sx={styles.recordLoading}>
        <CircularProgress aria-label={`Loading ${ariaLabel}`} />
      </Box>
    );
  }

  if (records.length === 0) {
    return (
      /* A report is read-only — nothing the user can create from here. */
      <EmptyState icon="report" title={emptyTitle} body={emptySub} />
    );
  }

  return (
    <>
      <Box sx={styles.selectionBar}>
        {/*
          A checkbox rather than a button: it reports "checked"/"mixed" the way
          the table header's own box did (WCAG 4.1.2). `ButtonBase` keeps the
          space/enter handling and the focus ring.
        */}
        <ButtonBase
          disableRipple
          role="checkbox"
          aria-checked={allSelected ? "true" : someSelected ? "mixed" : "false"}
          aria-label={`Select all ${ariaLabel}`}
          onClick={() => (allSelected ? onDeselectAll() : onSelectAll())}
          sx={styles.selectAllControl}
        >
          <Box
            aria-hidden="true"
            sx={styles.checkbox(allSelected || someSelected)}
          >
            {allSelected && <CheckGlyph sx={checkGlyphSx} />}
            {someSelected && <Box sx={styles.checkboxDash} />}
          </Box>
          <Typography component="span" sx={styles.selectAllLabel}>
            Select all
          </Typography>
        </ButtonBase>

        <Button
          variant="outlined"
          startIcon={<DownloadGlyph />}
          onClick={onDownload}
          disabled={selected.length === 0}
          sx={reportDownloadCompactSx}
        >
          {getReportDownloadLabel(selected.length)}
        </Button>
      </Box>

      <Box sx={styles.recordList} role="group" aria-label={ariaLabel}>
        {records.map((record) => {
          const checked = selected.includes(record.rowId);
          return (
            <ButtonBase
              key={record.rowId}
              disableRipple
              role="checkbox"
              aria-checked={checked}
              aria-label={record.selectionLabel}
              onClick={() =>
                checked
                  ? onDeselectOne(record.rowId)
                  : onSelectOne(record.rowId)
              }
              sx={styles.recordRow(checked)}
            >
              <Box aria-hidden="true" sx={styles.recordCheck}>
                <Box sx={styles.checkbox(checked)}>
                  {checked && <CheckGlyph sx={checkGlyphSx} />}
                </Box>
              </Box>

              <Box sx={styles.recordText}>
                <Typography component="span" sx={styles.recordName}>
                  {record.name}
                </Typography>
                {!!record.sub && (
                  <Typography component="span" sx={styles.recordSub}>
                    {record.sub}
                  </Typography>
                )}
              </Box>

              <Box sx={styles.recordPairs}>
                {record.pairs.map((pair) => (
                  <Typography
                    key={pair.label}
                    component="span"
                    sx={styles.recordPair}
                  >
                    {`${pair.label}: ${pair.value}`}
                  </Typography>
                ))}
              </Box>
            </ButtonBase>
          );
        })}
      </Box>

      {/*
        The reference lists every record at once and so draws no pager. The
        real report is paged, so a compact one appears only in the case the
        reference never shows — more rows than fit a page.
      */}
      {showPager && (
        <Box sx={styles.pager}>
          <Typography component="span" sx={styles.pagerLabel}>
            {`${firstOnPage}–${lastOnPage} of ${count}`}
          </Typography>
          <Box sx={{ display: "flex", gap: pager.gap }}>
            <ButtonBase
              disableRipple
              aria-label="Previous page"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
              sx={styles.pagerButton}
            >
              <ChevronGlyph
                sx={{
                  width: pager.iconSize,
                  height: pager.iconSize,
                  transform: "rotate(180deg)",
                }}
              />
            </ButtonBase>
            <ButtonBase
              disableRipple
              aria-label="Next page"
              disabled={page >= totalPages}
              onClick={() => onPageChange(page + 1)}
              sx={styles.pagerButton}
            >
              <ChevronGlyph
                sx={{ width: pager.iconSize, height: pager.iconSize }}
              />
            </ButtonBase>
          </Box>
        </Box>
      )}
    </>
  );
};

export default ReportsRecordList;
